import React, { useEffect, useRef, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";

/**
 * Presentation primitives shared by every dashboard card (FG-8053).
 *
 * A card is a titled panel with a source tag and a grid of label-above-value
 * fields. Putting the label above rather than in a right-aligned column buys
 * back about 40% of the width, which is what lets IDs and dates stop wrapping.
 */
export interface CardShellProps {
  title: string;
  /** Where the data comes from: Org, Zuora, Contract, Links. */
  source?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Drops the body padding so a divided grid can reach the card's edges. */
  flush?: boolean;
  testId?: string;
}

export const cardTitleSx = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
};

export const CardShell = ({
  title,
  source,
  action,
  children,
  flush,
  testId,
}: CardShellProps) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 1.5, overflow: "hidden", textAlign: "left" }}
    data-testid={testId}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 30,
        px: 1.5,
        py: 0.25,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "action.hover",
      }}
    >
      <Typography
        component="h3"
        sx={{
          ...cardTitleSx,
          minWidth: 0,
          flex: "0 1 auto",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>
      {action && <Box sx={{ flex: "none", display: "flex" }}>{action}</Box>}
      {source && (
        <Typography
          component="span"
          sx={{
            flex: "none",
            ml: "auto",
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.disabled",
            border: 1,
            borderColor: "divider",
            borderRadius: 0.75,
            px: 0.75,
          }}
        >
          {source}
        </Typography>
      )}
    </Box>
    <Box sx={flush ? undefined : { px: 1.5, py: 0.75 }}>{children}</Box>
  </Paper>
);

/** Column gap between fields, in the basis arithmetic as well as the grid. */
const FIELD_GAP = 24;

export interface FieldGridProps {
  children: React.ReactNode;
}

/**
 * Fields flow and stretch rather than sitting in equal columns.
 *
 * An equal-column grid gave "AU" the same width as an email address, so a wide
 * card ended up with dead space on the right while long values wrapped mid-token
 * anyway. Here each field asks for a basis suited to its content and grows into
 * whatever is left, so a row fills the card at any width.
 */
export const FieldGrid = ({ children }: FieldGridProps) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "flex-start",
      gap: `7px ${FIELD_GAP}px`,
    }}
  >
    {children}
  </Box>
);

/** How much room a field's content wants before it starts wrapping. */
export type FieldSize = "sm" | "md" | "lg" | "half";

/**
 * "half" claims exactly half the row - the gap included in the arithmetic - so
 * whatever follows it starts on the same edge as the second field of a
 * two-field row above or below. Without it each row finds its own column edge
 * and the card reads as ragged.
 */
const FIELD_BASIS: Record<FieldSize, number | string> = {
  sm: 90,
  md: 170,
  lg: 300,
  half: `calc(50% - ${FIELD_GAP / 2}px)`,
};

export interface FieldProps {
  label: string;
  children: React.ReactNode;
  /**
   * Sized to the content: sm for a code or count, which asks for no more width
   * than it needs; lg for an email or an id; half to hold a shared column edge.
   */
  size?: FieldSize;
  /** Note about where the value comes from, shown against the label. */
  hint?: string;
  /** Forces its own line - only for content that genuinely needs the width. */
  full?: boolean;
  /**
   * Label beside the value rather than above it. For a short value - a control,
   * a word - the label line is dead space, so this puts the label in the width
   * the value was never going to use, and two of them still share a row.
   */
  inline?: boolean;
  /**
   * The value cannot be broken or shortened - a date range reads as one thing.
   * The field keeps its content's full width and takes its own line when the
   * row cannot hold it, rather than letting the value spill over the card.
   */
  atomic?: boolean;
  mono?: boolean;
  testId?: string;
}

export const labelSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase" as const,
  color: "text.disabled",
  lineHeight: 1.3,
};

/** Base value typography, shared with anything presenting a field's worth of
 * data outside a card (e.g. a table cell) so it reads consistently. */
export const valueSx = {
  fontSize: 13.5,
  fontWeight: 600,
  lineHeight: 1.45,
  overflowWrap: "anywhere" as const,
};

export const valueMonoSx = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12.5,
  fontWeight: 500,
};

export const Field = ({
  label,
  children,
  size = "md",
  hint,
  full,
  inline,
  atomic,
  mono,
  testId,
}: FieldProps) => {
  const fieldValueSx = {
    ...valueSx,
    ...(mono ? valueMonoSx : {}),
  };

  if (inline) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          minWidth: 0,
          flexGrow: size === "half" || size === "sm" ? 0 : 1,
          flexShrink: 1,
          flexBasis: full ? "100%" : FIELD_BASIS[size],
          ...(atomic ? { minWidth: "max-content" } : null),
          ...(full ? { width: "100%" } : { maxWidth: "100%" }),
        }}
        data-testid={testId}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            flex: "none",
          }}
        >
          <Typography sx={labelSx}>{label}</Typography>
          {hint && <InfoHint title={hint} />}
        </Box>
        <Box sx={{ ...fieldValueSx, minWidth: 0 }}>{children}</Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        // A count or a code has nothing to do with extra width, so it does not
        // take a share of it - the field beside it usually does.
        flexGrow: size === "half" || size === "sm" ? 0 : 1,
        flexShrink: 1,
        flexBasis: full ? "100%" : FIELD_BASIS[size],
        ...(atomic ? { minWidth: "max-content" } : null),
        ...(full ? { width: "100%" } : { maxWidth: "100%" }),
      }}
      data-testid={testId}
    >
      {/* The hint belongs against the label - it describes the field, not the
          particular value sitting in it. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography sx={labelSx}>{label}</Typography>
        {hint && <InfoHint title={hint} />}
      </Box>
      <Box sx={fieldValueSx}>{children}</Box>
    </Box>
  );
};

export interface InfoHintProps {
  title: string;
}

/** The pane's inline note affordance: a tooltip on a small info IconButton. */
export const InfoHint = ({ title }: InfoHintProps) => (
  <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={8000}>
    <IconButton
      size="small"
      aria-label={title}
      sx={{ p: 0.125, ml: 0.25, color: "text.disabled" }}
    >
      <InfoIcon sx={{ fontSize: 13 }} />
    </IconButton>
  </Tooltip>
);

export interface CopyableProps {
  value: string;
  label: string;
  onCopy: (value: string) => void;
}

/**
 * A value that copies itself, so emails and ids never need a menu to get at.
 *
 * Kept to one line: a long address wrapping mid-token pushed the rest of the
 * card down and was no more readable for it. The tail goes to an ellipsis, and
 * the whole value stays in the tooltip and on the clipboard.
 */
export const Copyable = ({ value, label, onCopy }: CopyableProps) => (
  <Tooltip title={`${value} — click to copy ${label}`}>
    <Box
      component="button"
      type="button"
      onClick={() => onCopy(value)}
      aria-label={`Copy ${label}: ${value}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        cursor: "pointer",
        border: "none",
        background: "none",
        padding: 0,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Box>
      <ContentCopyIcon
        sx={{ fontSize: 12, flex: "none", color: "text.disabled" }}
      />
    </Box>
  </Tooltip>
);

export interface IdValueProps {
  value: string;
  label: string;
  onCopy: (value: string) => void;
  /**
   * The same id in its other form - a UUID against an s12 id. Offered as a
   * second copy action rather than a second line, since nobody reads either
   * one, they only ever paste it somewhere.
   */
  alternate?: { value: string; label: string };
  /** Matches the weight of the values around it, in the summary strip. */
  strong?: boolean;
  /**
   * A control belonging to this id - a link out to the record it names.
   *
   * It goes here rather than beside the component because this row has to take
   * the width its field has left, in order to know how much id fits. A sibling
   * would be pushed to the far edge by that same stretch, stranded a long way
   * from the id it acts on.
   */
  action?: React.ReactNode;
  /**
   * Namespaces the test ids. The same id appears in the collapsed strip and in
   * a card, and the strip's copy comes first in the DOM while hidden - one
   * prefix for both would leave a query picking the invisible one.
   */
  testIdPrefix?: string;
}

/** Gaps around the copy icon, in the measurement as well as the layout. */
const GAP_TEXT_TO_ICON = 4;
const GAP_ICON_TO_CHIP = 6;

/** Never shorten below this: two characters each side of the ellipsis. */
const MIN_ID_CHARS = 5;

/**
 * Keeps both ends of an id and elides the middle, symmetrically.
 *
 * Either end alone is ambiguous - ids from one system share a prefix, and a
 * tail could belong to anything - so what survives is the same number of
 * characters from each: "9a4a8c74-…21e396f5", then "9a4a…96f5" as the room
 * runs out.
 */
export const shortenIdToChars = (value: string, maxChars: number) => {
  if (maxChars >= value.length) {
    return value;
  }
  const keep = Math.max(
    2,
    Math.floor((Math.max(maxChars, MIN_ID_CHARS) - 1) / 2),
  );
  return `${value.slice(0, keep)}…${value.slice(-keep)}`;
};

/**
 * An identifier, as much of it as fits.
 *
 * A full UUID is 36 characters that are copied rather than read, so it must
 * never wrap onto a second line or push the copy controls out of reach - but in
 * a wide card there is room for all of it, and the whole value is easier to
 * recognise than an abbreviation. So it is measured: the field's width decides
 * how many characters survive, and the whole value stays one click and one
 * hover away regardless.
 */
/**
 * Width of a string as the given element would draw it.
 *
 * Measured on a canvas rather than with a hidden copy of the text in the DOM:
 * a duplicate would be read out by screen readers and picked up by any test
 * asserting on the field's text.
 */
const measureText = (text: string, element: HTMLElement): number => {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return 0;
  context.font = window.getComputedStyle(element).font;
  return context.measureText(text).width;
};

export const IdValue = ({
  value,
  label,
  onCopy,
  alternate,
  strong,
  testIdPrefix = "dashboard-id",
  action,
}: IdValueProps) => {
  const rootRef = useRef<HTMLElement>(null);
  const iconRef = useRef<SVGSVGElement>(null);
  const chipRef = useRef<HTMLElement>(null);
  const actionRef = useRef<HTMLElement>(null);
  const [maxChars, setMaxChars] = useState(value.length);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const fit = () => {
      // Measured on the whole field, minus the controls that sit beside the id.
      // Measuring the text itself only ever shrinks it: once shortened, its own
      // width becomes the space it thinks it has.
      const fullWidth = measureText(value, root);
      const icon = iconRef.current?.getBoundingClientRect().width ?? 0;
      const chip = chipRef.current?.getBoundingClientRect().width ?? 0;
      const control = actionRef.current?.getBoundingClientRect().width ?? 0;
      const available =
        root.getBoundingClientRect().width -
        icon -
        chip -
        control -
        GAP_TEXT_TO_ICON -
        (chip ? GAP_ICON_TO_CHIP : 0) -
        (control ? GAP_ICON_TO_CHIP : 0);
      // No measurable layout - a test environment, or a hidden card. Showing
      // the whole value is the honest fallback.
      if (!fullWidth || available <= 0) {
        setMaxChars(value.length);
        return;
      }
      setMaxChars(Math.floor((available / fullWidth) * value.length));
    };

    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(root);
    return () => observer.disconnect();
  }, [value]);

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: `${GAP_ICON_TO_CHIP}px`,
        minWidth: 0,
        // Takes the space its row has left rather than all of it: a sibling
        // control - the link out to Zuora, say - keeps its own width.
        flex: "1 1 auto",
      }}
    >
      <Tooltip title={`${value} — click to copy`}>
        <Box
          component="span"
          onClick={() => onCopy(value)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: `${GAP_TEXT_TO_ICON}px`,
            minWidth: 0,
            flex: "0 1 auto",
            cursor: "pointer",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12.5,
            fontWeight: strong ? 600 : 500,
            whiteSpace: "nowrap",
            "&:hover": { textDecoration: "underline" },
          }}
          data-testid={`${testIdPrefix}-${label}`}
        >
          <Box component="span" sx={{ flex: "0 1 auto", minWidth: 0 }}>
            {shortenIdToChars(value, maxChars)}
          </Box>
          <ContentCopyIcon
            ref={iconRef}
            sx={{ fontSize: 12, flex: "none", color: "text.disabled" }}
          />
        </Box>
      </Tooltip>
      {alternate && (
        <Tooltip title={`${alternate.value} — click to copy`}>
          <Box
            component="span"
            onClick={() => onCopy(alternate.value)}
            sx={{
              cursor: "pointer",
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.disabled",
              border: 1,
              borderColor: "divider",
              borderRadius: 0.75,
              px: 0.5,
              whiteSpace: "nowrap",
              "&:hover": { color: "text.secondary" },
            }}
            ref={chipRef}
            data-testid={`${testIdPrefix}-${label}-${alternate.label}`}
          >
            {alternate.label}
          </Box>
        </Tooltip>
      )}
      {action && (
        <Box ref={actionRef} sx={{ display: "inline-flex", flex: "none" }}>
          {action}
        </Box>
      )}
    </Box>
  );
};
